import React, { useEffect, useState } from "react";
import axios from "axios";
import io from "socket.io-client";
import { getApiBase } from "./apiBase";
import "./wall.css";

const API_BASE = getApiBase();
const socket = API_BASE ? io(API_BASE) : io();

export default function Wall(){
  const [posts, setPosts] = useState([]);
  const [latestPost, setLatestPost] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [popupQueue, setPopupQueue] = useState([]);
  const [newPostIds, setNewPostIds] = useState([]);

  const getPostKey = (post) => post?.postId || post?.id || post?._id;

  const dedupePosts = (list) => {
    const seen = new Set();
    const unique = [];
    for (const post of list) {
      const key = getPostKey(post);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      unique.push(post);
    }
    return unique;
  };

  const fetchPosts=async()=>{
    try {
      const res = await axios.get(`${API_BASE}/api/posts`);
      setPosts(dedupePosts(res.data));
    } catch (err) {
      console.error("Fetch error:", err.message);
    }
  };

  useEffect(()=>{
    fetchPosts();
    // Fast refresh from our DB (not Instagram API) — keeps wall in sync; sockets handle instant new_post.
    const dbPoll = setInterval(() => {
      fetchPosts();
    }, 5000);

    const handleNewPost = (post) => {
      console.log("New post received:", post);
      const incomingKey = getPostKey(post);
      setPosts((prev) => {
        if (!incomingKey) return prev;
        if (prev.some((p) => getPostKey(p) === incomingKey)) return prev;
        return [post, ...prev];
      });

      // Track new posts separately for temporary showcase highlighting.
      if (incomingKey) {
        setNewPostIds((prev) => (prev.includes(incomingKey) ? prev : [incomingKey, ...prev].slice(0, 20)));
        setTimeout(() => {
          setNewPostIds((prev) => prev.filter((id) => id !== incomingKey));
        }, 60000);
      }

      // Queue popups so bursts (e.g. 20 new posts) show one-by-one instead of only the latest.
      if (incomingKey) {
        setPopupQueue((prev) => {
          if (prev.some((p) => getPostKey(p) === incomingKey)) return prev;
          return [...prev, post];
        });
      }
    };

    socket.on("new_post", handleNewPost);

    return () => {
      clearInterval(dbPoll);
      socket.off("new_post", handleNewPost);
    };

  },[]);

  // If popup is not showing and we have queued items, show the next one.
  useEffect(() => {
    if (showPopup) return;
    if (!popupQueue.length) return;
    setLatestPost(popupQueue[0]);
    setShowPopup(true);
  }, [popupQueue, showPopup]);

  // Auto-hide popup after 10s, then advance the queue.
  useEffect(() => {
    if (!showPopup || !latestPost) return;
    const t = setTimeout(() => {
      setShowPopup(false);
      setPopupQueue((prev) => prev.slice(1));
    }, 10000);
    return () => clearTimeout(t);
  }, [showPopup, latestPost]);

  const getColumns = (posts) => {
    const cols = [[], [], [], []];
    posts.forEach((p, i) => {
      cols[i % 4].push(p);
    });
    return cols;
  };

  const columns = getColumns(posts);

  const hostedVideoSrc = (p) =>
    p.hosted_video_filename
      ? `${API_BASE}/media/videos/${encodeURIComponent(p.hosted_video_filename)}`
      : null;

  const renderMedia = (p) => {
    if (p.media_type === "VIDEO") {
      const hosted = hostedVideoSrc(p);
      const src = hosted || p.media_url;
      if (!src && p.thumbnail_url) {
        return <img src={p.thumbnail_url} alt={p.caption || ""} />;
      }
      if (!src) {
        return <div className="video-placeholder" aria-hidden />;
      }
      return (
        <video
          src={src}
          poster={p.thumbnail_url || undefined}
          autoPlay
          muted
          loop
          playsInline
          preload={hosted ? "auto" : "metadata"}
        />
      );
    }
    return <img src={p.media_url || p.thumbnail_url} alt={p.caption || ""} />;
  };

  return (
    <div className="wall-container">
      {posts.length === 0 ? (
        <div className="empty-state">
          <h1>Waiting for posts with #changeforbilimbe...</h1>
          <p>Tag your photos to see them here!</p>
        </div>
      ) : (
        <div className="wall">
          {columns.map((columnPosts, colIndex) => (
            <div 
              key={colIndex} 
              className={`column column-${colIndex + 1} ${(colIndex % 2 === 0) ? "up" : "down"}`}
            >
              {columnPosts.map((p, pIndex) => (
                <div className="card" key={getPostKey(p) || `${colIndex}-${pIndex}`}>
                  {newPostIds.includes(getPostKey(p)) && <span className="new-badge">NEW</span>}
                  {renderMedia(p)}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* New Post Popup */}
      {showPopup && latestPost && (
        <div className="new-post-popup">
          <div className="popup-overlay"></div>
          <div className="popup-content">
            <div className="popup-header">NEW POST SHARED</div>
            <div className="popup-media">
              {renderMedia(latestPost)}
            </div>
            <div className="popup-footer">
              <span className="hashtag">#changeforbilimbe</span>
              <div className="progress-bar"></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
